'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  baseUrl?: string;
  queryParams?: Record<string, string>;
  onPageChange?: (page: number) => void;
  colorScheme?: 'forest-green' | 'red';
  showAllPages?: boolean;
}

export default function Pagination({
  currentPage,
  totalPages,
  baseUrl = '',
  queryParams = {},
  onPageChange,
  colorScheme = 'forest-green',
  showAllPages = false,
}: PaginationProps) {
  const router = useRouter();

  // If only one page, don't show pagination
  if (totalPages <= 1) return null;

  const colorClasses = {
    'forest-green': {
      active: 'bg-forest-green-600 text-white',
      inactive:
        'bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700',
      hover:
        'hover:bg-forest-green-50 hover:text-forest-green-600 dark:hover:bg-forest-green-900/50 dark:hover:text-forest-green-400',
    },
    red: {
      active: 'bg-red-600 text-white',
      inactive:
        'bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700',
      hover: 'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-400',
    },
  };

  const colors = colorClasses[colorScheme];

  // Page 1 links to the bare URL so it isn't a duplicate of ?page=1
  const buildHref = (page: number) => {
    const params = new URLSearchParams(queryParams);
    if (page > 1) params.set('page', page.toString());
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    } else if (baseUrl) {
      router.push(buildHref(page));
    }
  };

  // Get pagination range (for when showAllPages is false)
  const getPaginationRange = () => {
    if (showAllPages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const range = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    const end = Math.min(totalPages, start + showPages - 1);

    if (end - start < showPages - 1) {
      start = Math.max(1, end - showPages + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const paginationRange = getPaginationRange();

  // Render page link/button
  const renderPageItem = (page: number, isActive = false) => {
    if (onPageChange) {
      // Client-side pagination
      return (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            isActive
              ? colors.active + ' shadow-lg'
              : colors.inactive + ' ' + colors.hover + ' shadow-sm'
          }`}
        >
          {page}
        </button>
      );
    } else {
      // URL-based pagination
      const href = baseUrl ? buildHref(page) : '#';
      return (
        <Link
          key={page}
          href={href}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            isActive ? colors.active : colors.inactive + ' ' + colors.hover
          }`}
        >
          {page}
        </Link>
      );
    }
  };

  return (
    <div className="flex justify-center items-center space-x-2 mt-8">
      {/* Previous Button */}
      {currentPage > 1 &&
        (onPageChange ? (
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label="Previous page"
            className={`px-4 py-2 rounded-lg font-medium transition-all ${colors.inactive} ${colors.hover} shadow-sm`}
          >
            <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        ) : (
          <Link
            href={buildHref(currentPage - 1)}
            aria-label="Previous page"
            className={`px-4 py-2 rounded-lg font-medium transition-all ${colors.inactive} ${colors.hover}`}
          >
            <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
        ))}

      {/* First Page */}
      {!showAllPages && paginationRange[0] > 1 && (
        <>
          {renderPageItem(1)}
          {paginationRange[0] > 2 && <span className="px-2 text-gray-400">...</span>}
        </>
      )}

      {/* Page Numbers */}
      {paginationRange.map((page) => renderPageItem(page, page === currentPage))}

      {/* Last Page */}
      {!showAllPages && paginationRange[paginationRange.length - 1] < totalPages && (
        <>
          {paginationRange[paginationRange.length - 1] < totalPages - 1 && (
            <span className="px-2 text-gray-400">...</span>
          )}
          {renderPageItem(totalPages)}
        </>
      )}

      {/* Next Button */}
      {currentPage < totalPages &&
        (onPageChange ? (
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label="Next page"
            className={`px-4 py-2 rounded-lg font-medium transition-all ${colors.inactive} ${colors.hover} shadow-sm`}
          >
            <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <Link
            href={buildHref(currentPage + 1)}
            aria-label="Next page"
            className={`px-4 py-2 rounded-lg font-medium transition-all ${colors.inactive} ${colors.hover}`}
          >
            <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        ))}
    </div>
  );
}
